/**
 * Frontend-local state management via localStorage.
 *
 * These features previously lived on the server but are now frontend-only:
 * - Conversation names: auto-generated from first user message
 * - Hidden turns: user can "delete" messages visually without server mutation
 * - Suggestions: hardcoded starter questions
 */

const STORAGE_KEY_NAMES = 'mrpibb_conversation_names';
const STORAGE_KEY_HIDDEN = 'mrpibb_hidden_turns';
const STORAGE_KEY_THOUGHTS = 'mrpibb_thought_process';
const STORAGE_KEY_TURN_RATINGS = 'mrpibb_turn_ratings';

let metadataApiUrl = '';
let authTokenFn: (() => string | null) | null = null;

/** Initialize the metadata API connection. Call once after auth is ready. */
export function initMetadataApi(apiUrl: string, getToken: () => string | null): void {
  metadataApiUrl = apiUrl;
  authTokenFn = getToken;
}

/** Sync metadata to the API (fire-and-forget, localStorage is the source of truth for reads) */
function syncToApi(conversationId: string): void {
  if (!metadataApiUrl || !authTokenFn) return;
  const token = authTokenFn();
  if (!token) return;

  const names = loadNames();
  const hidden = loadHidden();
  const thoughts = loadThoughts();

  // Collect thought process entries for this conversation
  const thoughtsForConv: Record<string, string[]> = {};
  const prefix = `${conversationId}:`;
  for (const [key, value] of Object.entries(thoughts)) {
    if (key.startsWith(prefix)) {
      const turnNum = key.slice(prefix.length);
      thoughtsForConv[turnNum] = value;
    }
  }

  const payload: Record<string, unknown> = {
    conversationId,
    name: names[conversationId] ?? undefined,
    hiddenTurns: hidden[conversationId] ?? [],
  };

  // Only include thoughtProcess when there's data to persist — avoids race condition
  // where a concurrent sync (e.g., from setConversationName) overwrites thoughts with {}
  if (Object.keys(thoughtsForConv).length > 0) {
    payload.thoughtProcess = thoughtsForConv;
  }

  fetch(`${metadataApiUrl}/metadata`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch(() => { /* fire-and-forget */ });
}

/** Load metadata from the API and merge into localStorage. Call on conversation load. */
export async function loadMetadataFromApi(conversationId: string): Promise<void> {
  if (!metadataApiUrl || !authTokenFn) return;
  const token = authTokenFn();
  if (!token) return;

  try {
    const res = await fetch(`${metadataApiUrl}/metadata/${encodeURIComponent(conversationId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const data = await res.json();

    // Merge into localStorage
    if (data.name) {
      const names = loadNames();
      names[conversationId] = data.name;
      saveNames(names);
    }
    if (data.hiddenTurns?.length) {
      const hidden = loadHidden();
      hidden[conversationId] = data.hiddenTurns;
      saveHidden(hidden);
    }
    if (data.thoughtProcess && Object.keys(data.thoughtProcess).length > 0) {
      const thoughts = loadThoughts();
      for (const [turnNum, steps] of Object.entries(data.thoughtProcess)) {
        thoughts[`${conversationId}:${turnNum}`] = steps as string[];
      }
      saveThoughts(thoughts);
    }
  } catch { /* non-critical */ }
}

// ─── Conversation Names ───

/** Get display name for a conversation, or undefined if not set */
export function getConversationName(conversationId: string): string | undefined {
  const names = loadNames();
  return names[conversationId];
}

/** Set display name for a conversation */
export function setConversationName(conversationId: string, name: string): void {
  const names = loadNames();
  names[conversationId] = name;
  saveNames(names);
  syncToApi(conversationId);
}

/** Remove name entry when a conversation is deleted */
export function removeConversationName(conversationId: string): void {
  const names = loadNames();
  delete names[conversationId];
  saveNames(names);
}

// ─── Hidden Turns ───

/** Get turn numbers that the user has hidden for a conversation */
export function getHiddenTurns(conversationId: string): number[] {
  const hidden = loadHidden();
  return hidden[conversationId] ?? [];
}

/** Hide a specific turn number (visually "delete" without server mutation) */
export function hideTurn(conversationId: string, turnNumber: number): void {
  const hidden = loadHidden();
  const turns = hidden[conversationId] ?? [];
  if (!turns.includes(turnNumber)) {
    turns.push(turnNumber);
    hidden[conversationId] = turns;
    saveHidden(hidden);
    syncToApi(conversationId);
  }
}

/** Remove all hidden-turn data when a conversation is deleted */
export function removeHiddenTurns(conversationId: string): void {
  const hidden = loadHidden();
  delete hidden[conversationId];
  saveHidden(hidden);
}

// ─── Thought Process ───

/** Store thought process for a specific turn */
export function setThoughtProcess(conversationId: string, turnNumber: number, thoughts: string[]): void {
  const all = loadThoughts();
  const key = `${conversationId}:${turnNumber}`;
  all[key] = thoughts;
  saveThoughts(all);
  syncToApi(conversationId);
}

/** Get stored thought process for a specific turn */
export function getThoughtProcess(conversationId: string, turnNumber: number): string[] | undefined {
  const all = loadThoughts();
  const key = `${conversationId}:${turnNumber}`;
  return all[key];
}

/** Remove all thought process data for a conversation (when deleted) */
export function removeThoughtProcesses(conversationId: string): void {
  const all = loadThoughts();
  const prefix = `${conversationId}:`;
  const updated: Record<string, string[]> = {};
  for (const [key, value] of Object.entries(all)) {
    if (!key.startsWith(prefix)) {
      updated[key] = value;
    }
  }
  saveThoughts(updated);
}

// ─── Turn Feedback (thumbs up/down) ───

/**
 * Keyed by `turnId` (a string), not `turnNumber` like every other function
 * in this file — deliberate, matching the backend's choice to key the
 * feedback table on the execution recorder's stable `turnId` rather than
 * the scan-derived, reusable `turnNumber` (see SodaBrainProtocol's
 * turn-feedback spec, Decisions Log entry 2, for why `turnNumber` is unfit
 * as a durable key: deleting the highest-numbered turn and generating a new
 * answer reassigns the same number). `TurnFeedback.tsx`/
 * `useConversationSessions.ts` never call these with a `turnNumber` at all.
 *
 * This restores locked feedback state on a same-browser reload only — it
 * does NOT sync across devices or survive a cleared cache, unlike names/
 * hidden-turns/thought-process above, which all call `syncToApi` to persist
 * through the metadata API. Turn ratings are deliberately NOT wired into
 * that sync (the metadata API's payload schema has no field for it, and
 * extending it is out of scope for this feature — see the spec's Trade-offs
 * on the reload gap). On a new device or cleared cache, a previously-rated
 * turn renders unlocked and converges to the true stored rating only on the
 * user's next interaction, via the backend's idempotent duplicate-ack path.
 */
export function setTurnRating(conversationId: string, turnId: string, rating: 'up' | 'down'): void {
  const all = loadTurnRatings();
  const key = `${conversationId}:${turnId}`;
  all[key] = rating;
  saveTurnRatings(all);
}

/** Get the locked-in rating for a specific turn, or undefined if never rated (in this browser). */
export function getTurnRating(conversationId: string, turnId: string): 'up' | 'down' | undefined {
  const all = loadTurnRatings();
  const key = `${conversationId}:${turnId}`;
  return all[key];
}

/**
 * Clear the locked-in rating for a single turn (decision 10 of the
 * turn-feedback spec: deselect/switch). Called on a `feedbackAck { status:
 * 'removed' }` — both for a plain deselect's terminal ack and for a
 * switch's intermediate one (see the spec's Decisions Log entry 12/§7: the
 * server-side row is genuinely gone at that exact moment in both cases, so
 * a same-browser reload must not restore a rating the server no longer
 * has). Distinct from `removeTurnRatings` (plural) below, which clears
 * every rating for an entire conversation on delete — this clears exactly
 * one `conversationId:turnId` entry.
 */
export function removeTurnRating(conversationId: string, turnId: string): void {
  const all = loadTurnRatings();
  const key = `${conversationId}:${turnId}`;
  delete all[key];
  saveTurnRatings(all);
}

/** Remove all turn-rating data for a conversation (when deleted) */
export function removeTurnRatings(conversationId: string): void {
  const all = loadTurnRatings();
  const prefix = `${conversationId}:`;
  const updated: Record<string, 'up' | 'down'> = {};
  for (const [key, value] of Object.entries(all)) {
    if (!key.startsWith(prefix)) {
      updated[key] = value;
    }
  }
  saveTurnRatings(updated);
}

// ─── Conversation Name Generation ───

/**
 * Extract a meaningful short name from the first user message.
 * Used to auto-name conversations in the sidebar.
 */
export function generateConversationName(question: string, date?: Date): string {
  const displayDate = (date ?? new Date()).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  const keywords = [
    'reign', 'multiview', 'rapid recap', 'clips', 'statsu', 'miro', 'event scout',
    'nba', 'nfl', 'mlb', 'nhl', 'mls', 'premier league', 'champions league',
    'wnba', 'ipl', 'la liga', 'serie a', 'bundesliga', 'ligue 1',
    'thursday night football', 'tnf', 'monday night football', 'mnf',
    'french open', 'roland garros', 'wimbledon', 'us open',
    'viewership', 'ccv', 'engagement', 'impressions', 'commerce',
    'lakers', 'celtics', 'knicks', 'warriors', 'chiefs', 'cowboys',
  ];

  const lower = question.toLowerCase();
  const found = keywords.filter(kw => lower.includes(kw));

  if (found.length > 0) {
    const label = found
      .slice(0, 2)
      .map(kw => kw.split(' ').map(w => w[0].toUpperCase() + w.slice(1)).join(' '))
      .join(' + ');
    return `${label} — ${displayDate}`;
  }

  // Fallback: use first meaningful words (skip filler words)
  const filler = new Set([
    'what', 'is', 'the', 'are', 'were', 'how', 'many', 'much', 'can', 'you',
    'show', 'me', 'get', 'for', 'in', 'of', 'a', 'an', 'to', 'do', 'did',
  ]);
  const words = question
    .replace(/[?!.,;:]/g, '')
    .split(/\s+/)
    .filter(w => !filler.has(w.toLowerCase()) && w.length > 1);
  const label = words.slice(0, 4).join(' ').slice(0, 35).trim();
  return `${label || 'Chat'} — ${displayDate}`;
}

// ─── Internal helpers ───

function loadNames(): Record<string, string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_NAMES);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveNames(names: Record<string, string>): void {
  localStorage.setItem(STORAGE_KEY_NAMES, JSON.stringify(names));
}

function loadHidden(): Record<string, number[]> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_HIDDEN);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveHidden(hidden: Record<string, number[]>): void {
  localStorage.setItem(STORAGE_KEY_HIDDEN, JSON.stringify(hidden));
}

function loadThoughts(): Record<string, string[]> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_THOUGHTS);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveThoughts(thoughts: Record<string, string[]>): void {
  localStorage.setItem(STORAGE_KEY_THOUGHTS, JSON.stringify(thoughts));
}

function loadTurnRatings(): Record<string, 'up' | 'down'> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_TURN_RATINGS);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveTurnRatings(ratings: Record<string, 'up' | 'down'>): void {
  localStorage.setItem(STORAGE_KEY_TURN_RATINGS, JSON.stringify(ratings));
}
